import React, { useState, useEffect, useCallback } from 'react';
import { LLMAnalysisEnhancement, UserRequest, Account } from '../types';
import {
  Copy,
  ThumbsUp,
  ThumbsDown,
  AlertTriangle,
  Heart,
  Activity,
  Stethoscope,
  User,
  Thermometer,
  Scale,
  Shield,
  ChevronDown,
  ChevronUp,
  Settings,
  Edit2
} from 'lucide-react';
import { api } from '../services/api';
import { usePlatformAuth } from '../contexts/AuthContext';
import { CombinedSoapNoteEditor } from './CombinedSoapNoteEditor';

interface PrenatalVisitNotesProps {
  llmAnalysisEnhancement: LLMAnalysisEnhancement | null;
  userRequest?: UserRequest | null;
  account?: Account | null;
}

export const PrenatalVisitNotesComponent: React.FC<PrenatalVisitNotesProps> = ({
  llmAnalysisEnhancement,
  userRequest,
  account: accountProp
}) => {
  const auth = usePlatformAuth();
  const [account, setAccount] = useState<Account | null>(accountProp || null);

  // Load account if not provided as prop
  useEffect(() => {
    if (accountProp) {
      setAccount(accountProp);
      return;
    }

    if (!auth.isAuthenticated) return;

    let cancelled = false;

    async function loadAccount() {
      try {
        const connectedId = import.meta.env.VITE_STRIPE_CONNECTED_ACCOUNT_ID;
        if (!connectedId) return;

        const idToken = await auth.getIdToken();
        if (!idToken) return;

        const acct = await api.getAccount(connectedId, idToken);
        if (!cancelled) {
          setAccount(acct);
        }
      } catch (err) {
        console.error('Failed to load account for placeholder replacement:', err);
      }
    }

    loadAccount();

    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, accountProp]);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    soap: true,
    'patient-summary': true,
    recommendations: true,
    'prenatal-extension': true,
    'key-concepts': true,
    'structured-extraction': true
  });

  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});
  const [feedbackStates, setFeedbackStates] = useState<Record<string, boolean | null>>({});
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState<string>('');
  const [saveToPatient, setSaveToPatient] = useState<boolean>(true);
  const [sending, setSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [editedSummary, setEditedSummary] = useState<string>('');

  // Combined SOAP note editing state
  const [editedCombinedSoapNote, setEditedCombinedSoapNote] = useState<string | undefined>(
    llmAnalysisEnhancement?.editedCombinedSoapNote
  );
  const [isSavingSoapNote, setIsSavingSoapNote] = useState(false);

  // Helper function to replace placeholders in patient summary
  const replacePlaceholders = (text: string): string => {
    if (!text) return text;
    let result = text;
    const clinicianName = account?.settings?.['Communication.ClinicianName'] || '';

    // Replace all variations of clinician name placeholders
    if (clinicianName) {
      result = result.replace(/\[Clinician's Name and Credentials\]/gi, clinicianName);
      result = result.replace(/\[Clinician Name and Credentials\]/gi, clinicianName);
      result = result.replace(/\[Clinician's Name\]/gi, clinicianName);
      result = result.replace(/\[Clinician Name\]/gi, clinicianName);
      result = result.replace(/\[Clinician\]/gi, clinicianName);
    }

    // Replace patient name placeholder with actual patient name if available
    if (userRequest?.patientName) {
      result = result.replace(/\[Patient Name\]/gi, userRequest.patientName);
      result = result.replace(/\[Patient's Name\]/gi, userRequest.patientName);
      result = result.replace(/\[Patient\]/gi, userRequest.patientName);
    }

    return result;
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const copyToClipboard = async (text: string, buttonKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [buttonKey]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [buttonKey]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleFeedback = (sectionName: string, isPositive: boolean) => {
    setFeedbackStates(prev => ({
      ...prev,
      [sectionName]: isPositive
    }));
    console.log(`Feedback for section ${sectionName}: ${isPositive ? 'positive' : 'negative'}`);
  };

  // Handle combined SOAP note edit save
  const handleCombinedSoapNoteSave = useCallback(
    async (editedCombinedNote: string) => {
      if (!userRequest?.id) {
        console.warn('No user request ID available for saving SOAP note edit');
        setEditedCombinedSoapNote(editedCombinedNote);
        return;
      }

      setIsSavingSoapNote(true);
      try {
        // Call API to save the edited combined SOAP note
        await api.saveCombinedSoapNoteEdit(userRequest.id, editedCombinedNote);

        // Update local state with the saved note
        setEditedCombinedSoapNote(editedCombinedNote);

        console.log('Combined SOAP note edit saved:', { editedCombinedNote });
      } catch (error) {
        console.error('Failed to save SOAP note edit:', error);
        alert('Failed to save SOAP note changes. Please try again.');
      } finally {
        setIsSavingSoapNote(false);
      }
    },
    [userRequest?.id]
  );

  const handleOpenSettings = () => {
    window.dispatchEvent(new CustomEvent('openSettings'));
  };

  const openSendModal = async () => {
    try {
      if (userRequest?.patientId) {
        const patient = await api.getPatient(userRequest.patientId);
        setEmailAddress(patient.email || '');
      }
      // Initialize editedSummary with replaced placeholders if not already set
      if (!editedSummary && prenatalData?.patientSummary) {
        setEditedSummary(replacePlaceholders(prenatalData.patientSummary));
      } else if (!editedSummary) {
        setEditedSummary(prenatalData?.patientSummary || '');
      }
    } catch (e) {
      console.warn('Failed to load patient for send modal', e);
    } finally {
      setIsSendModalOpen(true);
    }
  };

  const handleCloseSendModal = () => {
    setIsSendModalOpen(false);
    setShowSuccess(false);
  };

  const submitSend = async (summary: string) => {
    if (!userRequest?.patientId) {
      alert(
        'Please associate a patient with this request before sending. You can add a patient from the request details.'
      );
      return;
    }
    if (!summary?.trim()) {
      alert('No patient summary available to send.');
      return;
    }
    if (!emailAddress?.trim()) {
      alert('Please enter a valid email address.');
      return;
    }
    try {
      setSending(true);
      await api.sendPatientSummary(userRequest.patientId, {
        requestId: userRequest.id,
        channel: 'email',
        destination: emailAddress,
        saveToPatient,
        summary
      });
      setShowSuccess(true);
      setTimeout(() => {
        setIsSendModalOpen(false);
        setShowSuccess(false);
      }, 500);
    } catch (e: any) {
      alert(`Failed to send: ${e?.message || 'Unknown error'}`);
    } finally {
      setSending(false);
    }
  };

  // Formatting functions for copying human-readable text
  const formatkeyConcepts = (concepts: any[]): string => {
    let text = 'KEY CONCEPTS\n\n';
    concepts.forEach((concept, index) => {
      text += `${index + 1}. ${concept.label}\n`;
      text += `   Code: ${concept.code} (${concept.codeSystem})\n`;
      text += `   Justification: ${concept.justification}\n\n`;
    });
    return text;
  };

  const formatRecommendations = (recommendations: any[]): string => {
    let text = 'RECOMMENDATIONS\n\n';
    recommendations.forEach((rec, index) => {
      text += `${index + 1}. ${rec.action}\n`;
      text += `   Category: ${rec.category}\n`;
      text += `   Confidence: ${rec.confidence}\n`;
      text += `   Rationale: ${rec.rationale}\n\n`;
    });
    return text;
  };

  const formatstructuredExtraction = (extraction: any): string => {
    let text = 'STRUCTURED EXTRACTION\n\n';

    // demographicsAndIdentity
    if (extraction.demographicsAndIdentity) {
      text += 'DEMOGRAPHICS AND IDENTITY:\n';
      Object.entries(extraction.demographicsAndIdentity).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          const label = key.replace(/([A-Z])/g, ' $1').trim();
          text += `  ${label}: ${value}\n`;
        }
      });
      text += '\n';
    }

    // pregnancyOverview
    if (extraction.pregnancyOverview) {
      text += 'PREGNANCY OVERVIEW:\n';
      Object.entries(extraction.pregnancyOverview).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== 'highRiskReasons') {
          const label = key.replace(/([A-Z])/g, ' $1').trim();
          text += `  ${label}: ${value}\n`;
        }
      });
      if (extraction.pregnancyOverview.highRiskReasons && extraction.pregnancyOverview.highRiskReasons.length > 0) {
        text += `  High Risk Reasons: ${extraction.pregnancyOverview.highRiskReasons.join(', ')}\n`;
      }
      text += '\n';
    }

    // currentPregnancyStatus
    if (extraction.currentPregnancyStatus) {
      text += 'CURRENT PREGNANCY STATUS:\n';
      Object.entries(extraction.currentPregnancyStatus).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== 'primaryObProblemList') {
          const label = key.replace(/([A-Z])/g, ' $1').trim();
          text += `  ${label}: ${value}\n`;
        }
      });
      if (
        extraction.currentPregnancyStatus.primaryObProblemList &&
        extraction.currentPregnancyStatus.primaryObProblemList.length > 0
      ) {
        text += `  Primary OB Problems: ${extraction.currentPregnancyStatus.primaryObProblemList.join(', ')}\n`;
      }
      text += '\n';
    }

    // screeningAndPrenatalTests
    if (extraction.screeningAndPrenatalTests) {
      text += 'SCREENING AND PRENATAL TESTS:\n';
      Object.entries(extraction.screeningAndPrenatalTests).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== 'labsAndImagingThisVisit') {
          const label = key.replace(/([A-Z])/g, ' $1').trim();
          text += `  ${label}: ${value}\n`;
        }
      });
      if (
        extraction.screeningAndPrenatalTests.labsAndImagingThisVisit &&
        extraction.screeningAndPrenatalTests.labsAndImagingThisVisit.length > 0
      ) {
        text += `  Labs and Imaging This Visit: ${extraction.screeningAndPrenatalTests.labsAndImagingThisVisit.join(
          ', '
        )}\n`;
      }
      text += '\n';
    }

    // vaccinationsThisPregnancy
    if (extraction.vaccinationsThisPregnancy) {
      text += 'VACCINATIONS THIS PREGNANCY:\n';
      Object.entries(extraction.vaccinationsThisPregnancy).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          const label = key.replace(/([A-Z])/g, ' $1').trim();
          text += `  ${label}: ${value}\n`;
        }
      });
      text += '\n';
    }

    // vitalsAndBodyMetrics
    if (extraction.vitalsAndBodyMetrics) {
      text += 'VITALS AND BODY METRICS:\n';
      Object.entries(extraction.vitalsAndBodyMetrics).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          const label = key.replace(/([A-Z])/g, ' $1').trim();
          text += `  ${label}: ${value}\n`;
        }
      });
      text += '\n';
    }

    // symptomsAndConcerns
    if (extraction.symptomsAndConcerns) {
      text += 'SYMPTOMS AND CONCERNS:\n';
      if (extraction.symptomsAndConcerns.chiefComplaint) {
        text += `  Chief Complaint: ${extraction.symptomsAndConcerns.chiefComplaint}\n`;
      }
      if (extraction.symptomsAndConcerns.symptomList && extraction.symptomsAndConcerns.symptomList.length > 0) {
        extraction.symptomsAndConcerns.symptomList.forEach((symptom: any, index: number) => {
          text += `  ${index + 1}. ${symptom.name}: ${symptom.present ? 'Present' : 'Absent'}\n`;
          if (symptom.severity) text += `     Severity: ${symptom.severity}\n`;
          if (symptom.onsetOrPattern) text += `     Onset/Pattern: ${symptom.onsetOrPattern}\n`;
        });
      }
      text += '\n';
    }

    return text;
  };

  const renderSectionActions = (sectionName: string, content: string) => {
    const copyButtonKey = `${sectionName}-copy`;
    const isCopied = copiedStates[copyButtonKey];
    const feedback = feedbackStates[sectionName];

    return (
      <div className="flex items-center space-x-1">
        <button
          className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          onClick={() => copyToClipboard(content, copyButtonKey)}
          title="Copy to clipboard"
        >
          <Copy className="w-3 h-3 mr-1" />
          {isCopied ? 'Copied!' : 'Copy'}
        </button>
        <button
          className={`inline-flex items-center p-1 rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-300 ${feedback === true ? 'text-green-600 bg-green-50' : 'text-gray-500 hover:text-green-600 hover:bg-green-50'
            }`}
          onClick={() => handleFeedback(sectionName, true)}
          title="Good"
        >
          <ThumbsUp className="w-3 h-3" />
        </button>
        <button
          className={`inline-flex items-center p-1 rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-300 ${feedback === false ? 'text-red-600 bg-red-50' : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
            }`}
          onClick={() => handleFeedback(sectionName, false)}
          title="Needs improvement"
        >
          <ThumbsDown className="w-3 h-3" />
        </button>
      </div>
    );
  };

  const renderCodedConcept = (concept: any, index: number) => (
    <div key={index} className="card bg-base-100 shadow-sm border border-base-300">
      <div className="card-body p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="badge badge-primary badge-sm">{concept.codeSystem}</span>
          <span className="badge badge-outline badge-sm font-mono">{concept.code}</span>
        </div>
        <h4 className="card-title text-base">{concept.label}</h4>
        <p className="text-sm text-base-content/80">{concept.justification}</p>
      </div>
    </div>
  );

  const renderRecommendation = (recommendation: any, index: number) => (
    <div key={index} className="card bg-base-100 shadow-sm border border-base-300">
      <div className="card-body p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="badge badge-secondary badge-sm">{recommendation.category}</span>
          <span className="badge badge-outline badge-sm">{recommendation.confidence}</span>
        </div>
        <h4 className="card-title text-base">{recommendation.action}</h4>
        <p className="text-sm text-base-content/80">{recommendation.rationale}</p>
      </div>
    </div>
  );

  if (!llmAnalysisEnhancement?.prenatalOutput) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto flex items-center justify-center h-12 w-12 bg-orange-100 rounded-full mb-4">
          <AlertTriangle className="h-6 w-6 text-orange-600" />
        </div>
        <div className="max-w-md mx-auto">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Prenatal Visit Analysis Available</h3>
          <p className="text-gray-600 mb-4">No prenatal visit analysis was generated for this request.</p>
        </div>
      </div>
    );
  }

  const prenatalData = llmAnalysisEnhancement.prenatalOutput;

  // Initialize editedSummary with replaced placeholders when account, patient, or data changes
  const patientSummaryText = prenatalData?.patientSummary || '';

  useEffect(() => {
    if (!patientSummaryText) return;

    const replaced = replacePlaceholders(patientSummaryText);
    // Always update if replacement changed something or if editedSummary is empty
    if (replaced !== patientSummaryText || !editedSummary) {
      setEditedSummary(replaced);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.settings?.['Communication.ClinicianName'], userRequest?.patientName, patientSummaryText]);

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-base-content">Prenatal Visit</h3>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {/* SOAP Note - using CombinedSoapNoteEditor */}
          {prenatalData.structuredSoap && (
            <CombinedSoapNoteEditor
              originalNote={prenatalData.structuredSoap}
              editedCombinedNote={editedCombinedSoapNote}
              isExpanded={expandedSections['soap']}
              onToggleExpand={() => toggleSection('soap')}
              onSave={handleCombinedSoapNoteSave}
              isSaving={isSavingSoapNote}
              onCopy={text => copyToClipboard(text, 'soap-copy')}
              isCopied={copiedStates['soap-copy']}
              onFeedback={isPositive => handleFeedback('soap', isPositive)}
              feedbackState={feedbackStates['soap']}
            />
          )}

          {/* Patient Summary */}
          {prenatalData.patientSummary && (
            <div className="bg-base-100 border border-base-300 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-base-300">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleSection('patient-summary')}
                    className="p-1 hover:bg-base-200 rounded transition-colors"
                    title={expandedSections['patient-summary'] ? 'Collapse' : 'Expand'}
                  >
                    {expandedSections['patient-summary'] ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                  <span className="text-lg font-medium">Patient Summary</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap self-end sm:self-auto">
                  <button
                    onClick={handleOpenSettings}
                    className="btn btn-ghost btn-sm btn-circle"
                    title="Open settings to configure clinician name"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={openSendModal} title="Edit and send summary to patient">
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit & Send
                  </button>
                  {renderSectionActions('patient-summary', prenatalData.patientSummary)}
                </div>
              </div>
              {expandedSections['patient-summary'] && (
                <div className="p-4">
                  <p className="text-gray-800 leading-relaxed whitespace-pre-line">
                    {editedSummary || replacePlaceholders(prenatalData.patientSummary)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Recommendations */}
          {prenatalData.recommendations && prenatalData.recommendations.length > 0 && (
            <div className="bg-base-100 border border-base-300 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-base-300">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleSection('recommendations')}
                    className="p-1 hover:bg-base-200 rounded transition-colors"
                    title={expandedSections['recommendations'] ? 'Collapse' : 'Expand'}
                  >
                    {expandedSections['recommendations'] ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                  <span className="text-lg font-medium">Recommendations</span>
                </div>
                <div className="self-end sm:self-auto">
                  {renderSectionActions('recommendations', formatRecommendations(prenatalData.recommendations))}
                </div>
              </div>
              {expandedSections['recommendations'] && (
                <div className="p-4">
                  <div className="space-y-3">
                    {prenatalData.recommendations.map((recommendation, index) => renderRecommendation(recommendation, index))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Prenatal Visit Extension */}
          {prenatalData.prenatalVisitExtension && (
            <div className="bg-base-100 border border-base-300 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-base-300">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleSection('prenatal-extension')}
                    className="p-1 hover:bg-base-200 rounded transition-colors"
                    title={expandedSections['prenatal-extension'] ? 'Collapse' : 'Expand'}
                  >
                    {expandedSections['prenatal-extension'] ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                  <span className="text-lg font-medium">Prenatal Care Details</span>
                </div>
                <div className="self-end sm:self-auto">
                  {renderSectionActions('prenatal-extension', JSON.stringify(prenatalData.prenatalVisitExtension, null, 2))}
                </div>
              </div>
              {expandedSections['prenatal-extension'] && (
                <div className="p-4">
                  <div className="space-y-4">
                    {prenatalData.prenatalVisitExtension.trimester && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">Trimester</h4>
                          <p className="text-gray-800">{prenatalData.prenatalVisitExtension.trimester}</p>
                        </div>
                      </div>
                    )}
                    {prenatalData.prenatalVisitExtension.visitType && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">Visit Type</h4>
                          <p className="text-gray-800">{prenatalData.prenatalVisitExtension.visitType}</p>
                        </div>
                      </div>
                    )}
                    {prenatalData.prenatalVisitExtension.deliveryPlanningStatus && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">Delivery Planning</h4>
                          <p className="text-gray-800">{prenatalData.prenatalVisitExtension.deliveryPlanningStatus}</p>
                        </div>
                      </div>
                    )}
                    {prenatalData.prenatalVisitExtension.birthPlanNotes && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">Birth Plan Notes</h4>
                          <p className="text-gray-800">{prenatalData.prenatalVisitExtension.birthPlanNotes}</p>
                        </div>
                      </div>
                    )}
                    {prenatalData.prenatalVisitExtension.feedingPlans && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">Feeding Plans</h4>
                          <p className="text-gray-800">{prenatalData.prenatalVisitExtension.feedingPlans}</p>
                        </div>
                      </div>
                    )}
                    {prenatalData.prenatalVisitExtension.prenatalCounselingTopics &&
                      prenatalData.prenatalVisitExtension.prenatalCounselingTopics.length > 0 && (
                        <div className="card bg-base-100 shadow-sm border border-base-300">
                          <div className="card-body p-4">
                            <h4 className="text-base font-semibold text-gray-900 mb-2">Counseling Topics</h4>
                            <ul className="list-disc list-inside space-y-1">
                              {prenatalData.prenatalVisitExtension.prenatalCounselingTopics.map((topic, index) => (
                                <li key={index} className="text-gray-800">
                                  {topic}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    {prenatalData.prenatalVisitExtension.educationAndWarningSignsReviewed &&
                      prenatalData.prenatalVisitExtension.educationAndWarningSignsReviewed.length > 0 && (
                        <div className="card bg-base-100 shadow-sm border border-base-300">
                          <div className="card-body p-4">
                            <h4 className="text-base font-semibold text-gray-900 mb-2">Education and Warning Signs Reviewed</h4>
                            <ul className="list-disc list-inside space-y-1">
                              {prenatalData.prenatalVisitExtension.educationAndWarningSignsReviewed.map((sign, index) => (
                                <li key={index} className="text-gray-800">
                                  {sign}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    {prenatalData.prenatalVisitExtension.ordersPlacedThisVisit &&
                      prenatalData.prenatalVisitExtension.ordersPlacedThisVisit.length > 0 && (
                        <div className="card bg-base-100 shadow-sm border border-base-300">
                          <div className="card-body p-4">
                            <h4 className="text-base font-semibold text-gray-900 mb-2">Orders Placed This Visit</h4>
                            <ul className="list-disc list-inside space-y-1">
                              {prenatalData.prenatalVisitExtension.ordersPlacedThisVisit.map((order, index) => (
                                <li key={index} className="text-gray-800">
                                  {order}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    {prenatalData.prenatalVisitExtension.referralsThisVisit &&
                      prenatalData.prenatalVisitExtension.referralsThisVisit.length > 0 && (
                        <div className="card bg-base-100 shadow-sm border border-base-300">
                          <div className="card-body p-4">
                            <h4 className="text-base font-semibold text-gray-900 mb-2">Referrals This Visit</h4>
                            <ul className="list-disc list-inside space-y-1">
                              {prenatalData.prenatalVisitExtension.referralsThisVisit.map((referral, index) => (
                                <li key={index} className="text-gray-800">
                                  {referral}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    {prenatalData.prenatalVisitExtension.nextRecommendedVisitTiming && (
                      <div className="card bg-base-100 shadow-sm border border-base-300">
                        <div className="card-body p-4">
                          <h4 className="text-base font-semibold text-gray-900 mb-2">Next Recommended Visit</h4>
                          <p className="text-gray-800">{prenatalData.prenatalVisitExtension.nextRecommendedVisitTiming}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Key Concepts */}
          {prenatalData.keyConcepts && prenatalData.keyConcepts.length > 0 && (
            <div className="bg-base-100 border border-base-300 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-base-300">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleSection('key-concepts')}
                    className="p-1 hover:bg-base-200 rounded transition-colors"
                    title={expandedSections['key-concepts'] ? 'Collapse' : 'Expand'}
                  >
                    {expandedSections['key-concepts'] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  <span className="text-lg font-medium">Key Concepts</span>
                </div>
                <div className="self-end sm:self-auto">
                  {renderSectionActions('key-concepts', formatkeyConcepts(prenatalData.keyConcepts))}
                </div>
              </div>
              {expandedSections['key-concepts'] && (
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {prenatalData.keyConcepts.map((concept, index) => renderCodedConcept(concept, index))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Structured Extraction */}
          {prenatalData.structuredExtraction && (
            <div className="bg-base-100 border border-base-300 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-base-300">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleSection('structured-extraction')}
                    className="p-1 hover:bg-base-200 rounded transition-colors"
                    title={expandedSections['structured-extraction'] ? 'Collapse' : 'Expand'}
                  >
                    {expandedSections['structured-extraction'] ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                  <span className="text-lg font-medium">Clinical Data</span>
                </div>
                <div className="self-end sm:self-auto">
                  {renderSectionActions('structured-extraction', formatstructuredExtraction(prenatalData.structuredExtraction))}
                </div>
              </div>
              {expandedSections['structured-extraction'] && (
                <div className="p-4">
                  <div className="space-y-6">
                    {/* Pregnancy Overview */}
                    {prenatalData.structuredExtraction.pregnancyOverview && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          Pregnancy Overview
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {prenatalData.structuredExtraction.pregnancyOverview.gestationalAgeDisplay && (
                            <div className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">
                                  Gestational Age
                                </div>
                                <div className="text-base-content font-medium text-lg">
                                  {prenatalData.structuredExtraction.pregnancyOverview.gestationalAgeDisplay}
                                </div>
                              </div>
                            </div>
                          )}
                          {prenatalData.structuredExtraction.pregnancyOverview.estimatedDueDateIso && (
                            <div className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">
                                  Estimated Due Date
                                </div>
                                <div className="text-base-content font-medium">
                                  {new Date(
                                    prenatalData.structuredExtraction.pregnancyOverview.estimatedDueDateIso
                                  ).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          )}
                          {prenatalData.structuredExtraction.pregnancyOverview.gravidaPara && (
                            <div className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">
                                  Gravida/Para
                                </div>
                                <div className="text-base-content font-medium">
                                  {prenatalData.structuredExtraction.pregnancyOverview.gravidaPara}
                                </div>
                              </div>
                            </div>
                          )}
                          {prenatalData.structuredExtraction.pregnancyOverview.pregnancyRiskLevel && (
                            <div className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">
                                  Risk Level
                                </div>
                                <div className="text-base-content font-medium">
                                  {prenatalData.structuredExtraction.pregnancyOverview.pregnancyRiskLevel}
                                </div>
                              </div>
                            </div>
                          )}
                          {prenatalData.structuredExtraction.pregnancyOverview.multipleGestation !== null && (
                            <div className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">
                                  Multiple Gestation
                                </div>
                                <div className="text-base-content font-medium">
                                  {prenatalData.structuredExtraction.pregnancyOverview.multipleGestation ? 'Yes' : 'No'}
                                </div>
                              </div>
                            </div>
                          )}
                          {prenatalData.structuredExtraction.pregnancyOverview.numberOfFetuses && (
                            <div className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">
                                  Number of Fetuses
                                </div>
                                <div className="text-base-content font-medium">
                                  {prenatalData.structuredExtraction.pregnancyOverview.numberOfFetuses}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {prenatalData.structuredExtraction.pregnancyOverview.highRiskReasons &&
                          prenatalData.structuredExtraction.pregnancyOverview.highRiskReasons.length > 0 && (
                            <div className="mt-4">
                              <div className="text-sm font-semibold text-base-content/70 mb-2">High Risk Reasons:</div>
                              <div className="flex flex-wrap gap-2">
                                {prenatalData.structuredExtraction.pregnancyOverview.highRiskReasons.map((reason, index) => (
                                  <span key={index} className="badge badge-error badge-sm">
                                    {reason}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    )}

                    {/* Current Pregnancy Status */}
                    {prenatalData.structuredExtraction.currentPregnancyStatus && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Current Pregnancy Status</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {prenatalData.structuredExtraction.currentPregnancyStatus.fetalHeartRateBpm && (
                            <div className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <Heart className="w-4 h-4 text-base-content/80" />
                                  <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold">
                                    Fetal Heart Rate
                                  </div>
                                </div>
                                <div className="text-base-content font-medium">
                                  {prenatalData.structuredExtraction.currentPregnancyStatus.fetalHeartRateBpm} bpm
                                </div>
                              </div>
                            </div>
                          )}
                          {prenatalData.structuredExtraction.currentPregnancyStatus.fundalHeightCm && (
                            <div className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <Scale className="w-4 h-4 text-base-content/80" />
                                  <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold">
                                    Fundal Height
                                  </div>
                                </div>
                                <div className="text-base-content font-medium">
                                  {prenatalData.structuredExtraction.currentPregnancyStatus.fundalHeightCm} cm
                                </div>
                              </div>
                            </div>
                          )}
                          {prenatalData.structuredExtraction.currentPregnancyStatus.fundalHeightComment && (
                            <div className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">
                                  Fundal Height Comment
                                </div>
                                <div className="text-base-content font-medium">
                                  {prenatalData.structuredExtraction.currentPregnancyStatus.fundalHeightComment}
                                </div>
                              </div>
                            </div>
                          )}
                          {prenatalData.structuredExtraction.currentPregnancyStatus.fetalMovementDescription && (
                            <div className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-3">
                                <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold mb-1">
                                  Fetal Movement
                                </div>
                                <div className="text-base-content font-medium">
                                  {prenatalData.structuredExtraction.currentPregnancyStatus.fetalMovementDescription}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {prenatalData.structuredExtraction.currentPregnancyStatus.primaryObProblemList &&
                          prenatalData.structuredExtraction.currentPregnancyStatus.primaryObProblemList.length > 0 && (
                            <div className="mt-4">
                              <div className="text-sm font-semibold text-base-content/70 mb-2">Primary OB Problems:</div>
                              <div className="flex flex-wrap gap-2">
                                {prenatalData.structuredExtraction.currentPregnancyStatus.primaryObProblemList.map(
                                  (problem, index) => (
                                    <span key={index} className="badge badge-warning badge-sm">
                                      {problem}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        {(prenatalData.structuredExtraction.currentPregnancyStatus.contractionsDescription ||
                          prenatalData.structuredExtraction.currentPregnancyStatus.vaginalBleedingDescription ||
                          prenatalData.structuredExtraction.currentPregnancyStatus.leakageOfFluidDescription ||
                          prenatalData.structuredExtraction.currentPregnancyStatus.preeclampsiaSymptomsDescription) && (
                            <div className="mt-4 space-y-2">
                              {prenatalData.structuredExtraction.currentPregnancyStatus.contractionsDescription && (
                                <div className="text-sm">
                                  <span className="font-semibold">Contractions: </span>
                                  <span>{prenatalData.structuredExtraction.currentPregnancyStatus.contractionsDescription}</span>
                                </div>
                              )}
                              {prenatalData.structuredExtraction.currentPregnancyStatus.vaginalBleedingDescription && (
                                <div className="text-sm">
                                  <span className="font-semibold">Vaginal Bleeding: </span>
                                  <span>
                                    {prenatalData.structuredExtraction.currentPregnancyStatus.vaginalBleedingDescription}
                                  </span>
                                </div>
                              )}
                              {prenatalData.structuredExtraction.currentPregnancyStatus.leakageOfFluidDescription && (
                                <div className="text-sm">
                                  <span className="font-semibold">Leakage of Fluid: </span>
                                  <span>
                                    {prenatalData.structuredExtraction.currentPregnancyStatus.leakageOfFluidDescription}
                                  </span>
                                </div>
                              )}
                              {prenatalData.structuredExtraction.currentPregnancyStatus.preeclampsiaSymptomsDescription && (
                                <div className="text-sm">
                                  <span className="font-semibold">Preeclampsia Symptoms: </span>
                                  <span>
                                    {prenatalData.structuredExtraction.currentPregnancyStatus.preeclampsiaSymptomsDescription}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    )}

                    {/* DemographicsAndIdentity */}
                    {prenatalData.structuredExtraction.demographicsAndIdentity && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Demographics and Identity</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {Object.entries(prenatalData.structuredExtraction.demographicsAndIdentity).map(
                            ([key, value]) =>
                              value !== null &&
                              value !== undefined && (
                                <div key={key} className="card bg-base-100 shadow-sm border border-base-300">
                                  <div className="card-body p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                      <User className="w-4 h-4 text-base-content/80" />
                                      <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold">
                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                      </div>
                                    </div>
                                    <div className="text-base-content font-medium">{String(value)}</div>
                                  </div>
                                </div>
                              )
                          )}
                        </div>
                      </div>
                    )}

                    {/* ScreeningAndPrenatalTests */}
                    {prenatalData.structuredExtraction.screeningAndPrenatalTests && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Screening and Prenatal Tests</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {Object.entries(prenatalData.structuredExtraction.screeningAndPrenatalTests).map(
                            ([key, value]) =>
                              value !== null &&
                              value !== undefined &&
                              key !== 'labsAndImagingThisVisit' && (
                                <div key={key} className="card bg-base-100 shadow-sm border border-base-300">
                                  <div className="card-body p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Shield className="w-4 h-4 text-base-content/80" />
                                      <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold">
                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                      </div>
                                    </div>
                                    <div className="text-base-content font-medium">{String(value)}</div>
                                  </div>
                                </div>
                              )
                          )}
                        </div>
                        {prenatalData.structuredExtraction.screeningAndPrenatalTests.labsAndImagingThisVisit &&
                          prenatalData.structuredExtraction.screeningAndPrenatalTests.labsAndImagingThisVisit.length >
                          0 && (
                            <div className="mt-4">
                              <div className="text-sm font-semibold text-base-content/70 mb-2">
                                Labs and Imaging This Visit:
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {prenatalData.structuredExtraction.screeningAndPrenatalTests.labsAndImagingThisVisit.map(
                                  (lab, index) => (
                                    <span key={index} className="badge badge-info badge-sm">
                                      {lab}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    )}

                    {/* VaccinationsThisPregnancy */}
                    {prenatalData.structuredExtraction.vaccinationsThisPregnancy && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Vaccinations This Pregnancy</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {Object.entries(prenatalData.structuredExtraction.vaccinationsThisPregnancy).map(
                            ([key, value]) =>
                              value !== null &&
                              value !== undefined && (
                                <div key={key} className="card bg-base-100 shadow-sm border border-base-300">
                                  <div className="card-body p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Shield className="w-4 h-4 text-base-content/80" />
                                      <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold">
                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                      </div>
                                    </div>
                                    <div className="text-base-content font-medium">{String(value)}</div>
                                  </div>
                                </div>
                              )
                          )}
                        </div>
                      </div>
                    )}

                    {/* VitalsAndBodyMetrics */}
                    {prenatalData.structuredExtraction.vitalsAndBodyMetrics && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Vitals and Body Metrics</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {Object.entries(prenatalData.structuredExtraction.vitalsAndBodyMetrics).map(([key, value]) => (
                            value !== null &&
                            value !== undefined && (
                              <div key={key} className="card bg-base-100 shadow-sm border border-base-300">
                                <div className="card-body p-3">
                                  <div className="flex items-center gap-2 mb-1">
                                    {key.toLowerCase().includes('temperature') ? (
                                      <Thermometer className="w-4 h-4 text-base-content/80" />
                                    ) : key.toLowerCase().includes('weight') ||
                                      key.toLowerCase().includes('height') ||
                                      key.toLowerCase().includes('bmi') ? (
                                      <Scale className="w-4 h-4 text-base-content/80" />
                                    ) : (
                                      <Activity className="w-4 h-4 text-base-content/80" />
                                    )}
                                    <div className="text-xs text-base-content/80 uppercase tracking-wide font-semibold">
                                      {key.replace(/([A-Z])/g, ' $1').trim()}
                                    </div>
                                  </div>
                                  <div className="text-base-content font-medium">{String(value)}</div>
                                </div>
                              </div>
                            )
                          ))}
                        </div>
                      </div>
                    )}

                    {/* SymptomsAndConcerns */}
                    {prenatalData.structuredExtraction.symptomsAndConcerns && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Symptoms and Concerns</h4>
                        <div className="space-y-4">
                          {prenatalData.structuredExtraction.symptomsAndConcerns.chiefComplaint && (
                            <div className="card bg-base-100 shadow-sm border border-base-300">
                              <div className="card-body p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <Stethoscope className="w-5 h-5 text-base-content" />
                                  <h4 className="card-title text-base">Chief Complaint</h4>
                                </div>
                                <p className="text-base-content">{prenatalData.structuredExtraction.symptomsAndConcerns.chiefComplaint}</p>
                              </div>
                            </div>
                          )}
                          {prenatalData.structuredExtraction.symptomsAndConcerns.symptomList &&
                            prenatalData.structuredExtraction.symptomsAndConcerns.symptomList.length > 0 && (
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                {prenatalData.structuredExtraction.symptomsAndConcerns.symptomList.map(
                                  (symptom: any, index: number) => (
                                    <div
                                      key={index}
                                      className={`card shadow-sm border ${symptom.present ? 'border-error' : 'border-base-300'
                                        } bg-base-100`}
                                    >
                                      <div className="card-body p-3">
                                        <div className="flex items-center gap-2 mb-1">
                                          <Stethoscope
                                            className={`w-4 h-4 ${symptom.present ? 'text-error' : 'text-base-content/80'}`}
                                          />
                                          <div
                                            className={`text-xs uppercase tracking-wide font-semibold ${symptom.present ? 'text-error' : 'text-base-content/80'
                                              }`}
                                          >
                                            {symptom.name}
                                          </div>
                                        </div>
                                        <div
                                          className={`font-medium ${symptom.present ? 'text-error' : 'text-base-content/70'
                                            }`}
                                        >
                                          {symptom.present ? 'Present' : 'Absent'}
                                        </div>
                                        {symptom.severity && (
                                          <div
                                            className={`text-sm mt-1 ${symptom.present ? 'text-error' : 'text-base-content/60'
                                              }`}
                                          >
                                            Severity: {symptom.severity}
                                          </div>
                                        )}
                                        {symptom.onsetOrPattern && (
                                          <div
                                            className={`text-sm mt-1 ${symptom.present ? 'text-error' : 'text-base-content/60'
                                              }`}
                                          >
                                            Onset/Pattern: {symptom.onsetOrPattern}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit & Send Summary Modal */}
      {isSendModalOpen && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-3xl">
            <h3 className="font-bold text-lg mb-4">Edit & Send Summary to Patient</h3>
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Patient Summary</span>
                </label>
                <textarea
                  value={editedSummary}
                  onChange={e => setEditedSummary(e.target.value)}
                  className="textarea textarea-bordered w-full min-h-[300px] font-mono text-sm"
                  placeholder="Edit patient summary..."
                  disabled={sending}
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Email address</span>
                </label>
                <input
                  type="email"
                  placeholder="name@example.com"
                  className="input input-bordered w-full"
                  value={emailAddress}
                  onChange={e => setEmailAddress(e.target.value)}
                  disabled={sending}
                />
              </div>

              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-2">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={saveToPatient}
                    onChange={e => setSaveToPatient(e.target.checked)}
                    disabled={sending}
                  />
                  <span className="label-text">Save email to patient profile</span>
                </label>
              </div>
            </div>
            {showSuccess && (
              <div className="text-center text-success text-sm mt-4 mb-2">Patient summary sent successfully!</div>
            )}
            <div className="modal-action mt-6">
              <button className="btn btn-ghost" onClick={handleCloseSendModal} disabled={sending}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => submitSend(editedSummary)}
                disabled={sending || !emailAddress.trim() || !editedSummary.trim()}
              >
                {sending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={handleCloseSendModal}>
            <button>close</button>
          </form>
        </dialog>
      )}
    </>
  );
};
